/**
 * Generate a stable Idempotency-Key for a mutation. The same key must be
 * reused across retries of one logical submit so the server replays the
 * original response (see internal/api/handlers idempotency helpers).
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `idk-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Schema-path templates (not resolved URLs) copied verbatim from
// web/src/lib/api/idempotency.ts. Exceptions either do not mutate state or
// return a secret that the generic idempotency store must never persist or
// replay. Do not re-derive this list from intuition — port it exactly.
const AUTOMATIC_IDEMPOTENCY_EXCEPTION_TEMPLATES = [
  "/api/v1/auth/login",
  "/api/v1/auth/refresh",
  "/api/v1/auth/logout",
  "/admin/v1/agents/migrations/preview",
  "/admin/v1/nodes/provisioning-bundles",
  "/admin/v1/nodes/{nodeID}/provisioning-bundles",
  "/admin/v1/nodes/{nodeID}/adapter-tokens",
  "/admin/v1/nodes/{nodeID}/adapter-tokens/{tokenID}/rotate",
  "/admin/v1/enrollment-tokens",
  "/admin/v1/enrollment-tokens/verify",
  "/api/v1/subscription-links/{linkID}/rotate-secret",
  "/internal/v1/node/manifest/ack",
  "/internal/v1/node/manifest/heartbeat",
  "/internal/v1/node/boot/close",
  "/internal/v1/node/usage-reports",
  "/api/v1/agents/{agent_id}/install-report",
  "/api/v1/agents/{agent_id}/heartbeats",
  "/api/v1/nodes/{node_id}/traffic-reports",
  "/api/v1/nodes/{node_id}/heartbeats",
  "/api/v1/nodes/{node_id}/install-report",
  "/api/v1/nodes/{node_id}/adapter-token/rotate",
];

function escapeRegExp(segment: string): string {
  return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Our fetch mutator only sees the resolved URL (e.g. `/admin/v1/nodes/<uuid>/adapter-tokens`),
// not openapi-fetch's schemaPath template — so each exception template is
// compiled into a regex with `{param}` segments matching any path segment.
const AUTOMATIC_IDEMPOTENCY_EXCEPTION_PATTERNS = AUTOMATIC_IDEMPOTENCY_EXCEPTION_TEMPLATES.map(
  (template) =>
    new RegExp(
      `^${template
        .split("/")
        .map((segment) => (/^\{.+\}$/.test(segment) ? "[^/]+" : escapeRegExp(segment)))
        .join("/")}$`,
    ),
);

function matchesIdempotencyException(pathname: string): boolean {
  return AUTOMATIC_IDEMPOTENCY_EXCEPTION_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Return whether the SPA client should add a generic Idempotency-Key.
 * `url` is the resolved request path (query string, if any, is ignored).
 */
export function shouldAutomaticallyAttachIdempotencyKey(method: string, url: string): boolean {
  const pathname = url.split("?")[0];
  return MUTATION_METHODS.has(method.toUpperCase()) && !matchesIdempotencyException(pathname);
}

/**
 * Add a generated Idempotency-Key to `headers` in place, unless the request
 * doesn't qualify (see `shouldAutomaticallyAttachIdempotencyKey`) or the
 * caller already supplied one. Pure enough to unit test without a real
 * fetch client — `api/client.ts`'s onRequest hook just calls this.
 */
export function applyIdempotencyKey(
  headers: Headers,
  method: string,
  url: string,
  keyFactory: () => string = newIdempotencyKey,
): void {
  if (shouldAutomaticallyAttachIdempotencyKey(method, url) && !headers.has("Idempotency-Key")) {
    headers.set("Idempotency-Key", keyFactory());
  }
}
